/**
 * React context and provider for the MasterView Zustand store.
 *
 * Each MasterViewProvider instance creates an isolated store scoped to its
 * subtree. The useMasterViewStore hook reads from the nearest provider.
 *
 * @internal
 * @module store/MasterViewContext
 * @see FMPV-MVWC-E001-S003 — Context isolation requirement
 */
import React, { createContext, useContext, useRef, type ReactNode } from 'react';
import { useStore } from 'zustand';
import type { StoreApi } from 'zustand';
import type { MasterPatternViewEntity } from '../types/service.types';
import type { MasterPatternViewDefinition } from '../types/definition.types';
import type { MasterViewState } from './types';
import { createMasterViewStore } from './createMasterViewStore';

const MasterViewContext = createContext<StoreApi<MasterViewState<any>> | null>(null);

export interface MasterViewProviderProps<T extends MasterPatternViewEntity> {
  children: ReactNode;
  definition: MasterPatternViewDefinition;
  defaultCreateValues?: Record<string, unknown>;
  /** Initial `searchParams.pageSize` — mirrors the host's own `pagination.defaultPageSize`. */
  initialPageSize?: number;
}

/**
 * Provider that creates and holds an isolated store instance.
 * The store is created once on first render (via useRef) and remains stable.
 */
export function MasterViewProvider<T extends MasterPatternViewEntity>({
  children,
  definition,
  defaultCreateValues,
  initialPageSize,
}: MasterViewProviderProps<T>) {
  const storeRef = useRef<StoreApi<MasterViewState<T>> | undefined>(undefined);
  if (!storeRef.current) {
    storeRef.current = createMasterViewStore<T>(definition, defaultCreateValues, initialPageSize);
  }

  return (
    <MasterViewContext.Provider value={storeRef.current}>
      {children}
    </MasterViewContext.Provider>
  );
}

/**
 * Hook to access the nearest MasterView store via context.
 * Throws if used outside a MasterViewProvider.
 *
 * @param selector - Selector function to pick state from the store
 * @returns The selected state value
 */
export function useMasterViewStore<U>(
  selector: (state: MasterViewState<any>) => U,
): U {
  const store = useContext(MasterViewContext);
  if (!store) {
    throw new Error('useMasterViewStore must be used within MasterViewProvider');
  }
  return useStore(store, selector);
}
